import {
  ugcAiScorePath,
  ugcCompliancePath,
  ugcCvrPath,
  ugcDashboardPath,
  ugcDisplayRulesPath,
  ugcGuidePath,
  ugcInstagramPath,
  ugcInstagramSettingsPath,
  ugcMailPath,
  ugcProductTaggingPath,
  ugcReviewsPath,
  ugcSeoPath,
  ugcTagEmbedPath,
  ugcVocPath,
  ugcWearPath
} from '@/common/app/path'

/**
 * UGC/レビューのメニュー定義（2026-09-29 追加）。
 *
 * サイドメニュー（AppNavigation）と設定ガイド（UGCGuide）が**同じこの定義**を見る。
 * ガイドは「ここにある機能」だけを案内し、準備中の機能を出さない。
 * 新しい画面ができたら、ここに1行足せばメニューとガイドの両方に出る
 * （ガイド側で準備中として持っている手順が、その時点で自動的に表示される）。
 */

/** いまメニューにある機能 */
export type UgcMenuKey =
  | 'dashboard'
  | 'cvr'
  | 'seo'
  | 'voc'
  | 'wear'
  | 'mail'
  | 'reviews'
  | 'instagram'
  | 'instagramSettings'
  | 'aiScore'
  | 'productTagging'
  | 'compliance'
  | 'displayRules'
  | 'tagEmbed'
  | 'guide'

/**
 * まだメニューに無い（準備中の）機能。ガイドの手順はこのキーでも書いておける。
 * 画面ができたら、ここから UgcMenuKey へ移し、UGC_MENU に追加する
 */
export type UgcPlannedKey = 'abTest' | 'gifting' | 'staff' | 'qa' | 'omo' | 'video' | 'portal' | 'loyalty'

export type UgcFeatureKey = UgcMenuKey | UgcPlannedKey

export interface UgcMenuEntry {
  key: UgcMenuKey
  name: string
  /** 設定ガイドの機能一覧に出す一行説明 */
  desc: string
  path(siteId: number): string
  /**
   * 選択状態を完全一致で判定する。
   * Instagram UGC は設定側のパスが収集側を接頭辞に含むため、startsWith だと設定を開いたときにも選択される
   */
  exact?: boolean
}

export interface UgcMenuGroup {
  name: string
  items: UgcMenuEntry[]
}

export type UgcMenuNode = UgcMenuEntry | UgcMenuGroup

export const isUgcMenuGroup = (n: UgcMenuNode): n is UgcMenuGroup => 'items' in n

/** 並び順がそのままサイドメニューの並び順になる */
export const UGC_MENU: UgcMenuNode[] = [
  { key: 'dashboard', name: 'ダッシュボード', desc: '依頼→投稿→公開→表示→購入の流れと、いまやるべきこと', path: ugcDashboardPath },
  {
    name: '効果・分析',
    items: [
      { key: 'cvr', name: 'CVRダッシュボード', desc: 'UGCを見た人と見ていない人の購入率の比較、日別の推移、流入経路別の成果', path: ugcCvrPath },
      { key: 'seo', name: 'SEOダッシュボード', desc: 'Google検索に★とレビュー数を出すための構造化データの確認', path: ugcSeoPath },
      { key: 'voc', name: 'VOC/NPS分析', desc: 'レビューの頻出キーワード・満足度（NPS）と、LP向けコピーのAI生成', path: ugcVocPath },
      { key: 'wear', name: 'コンテンツ鮮度アラート', desc: '見飽きられてクリック率が落ちた投稿の検出と、入れ替えの提案', path: ugcWearPath }
    ]
  },
  { key: 'mail', name: 'レビュー依頼管理', desc: '購入者へのレビュー依頼メール、投稿フォーム、送信状況、投稿特典の管理', path: ugcMailPath },
  { key: 'reviews', name: '投稿レビュー管理', desc: 'レビューの確認・公開・返信・社内メモ、CSVでの取り込み', path: ugcReviewsPath },
  { key: 'instagram', name: 'Instagram UGC', desc: 'Instagram の投稿の収集と、紹介の許諾依頼', path: ugcInstagramPath, exact: true },
  { key: 'instagramSettings', name: 'Instagram設定', desc: 'Instagram の接続、収集するハッシュタグと頻度、ブランド情報', path: ugcInstagramSettingsPath },
  { key: 'aiScore', name: 'AIスコアリング設定', desc: 'AIによる投稿の採点と、スコアでの自動振り分け', path: ugcAiScorePath },
  { key: 'productTagging', name: 'UGC 商品タグ付与', desc: '写真の上に商品を紐づけて、写真から商品ページへ誘導', path: ugcProductTaggingPath },
  { key: 'compliance', name: '削除要望対応', desc: 'お客様からの削除依頼に応じた、個人データの確認と匿名化', path: ugcCompliancePath },
  // 2026-09-29 変更：枠を作ってからタグを貼る順番なので、表示枠設定を配信タグ設定より上にした
  {
    name: 'サイト表示',
    items: [
      { key: 'displayRules', name: 'サイト表示枠設定', desc: 'サイトに出す枠（内容・レイアウト・並び順・件数）の作成', path: ugcDisplayRulesPath },
      { key: 'tagEmbed', name: '配信タグ設定', desc: 'ECサイトに貼るタグの発行と、ページごとの使い分け', path: ugcTagEmbedPath }
    ]
  },
  { key: 'guide', name: '設定ガイド', desc: 'このガイド', path: ugcGuidePath }
]

/** 入れ子を平らにした一覧 */
export const UGC_MENU_ENTRIES: UgcMenuEntry[] = UGC_MENU.flatMap((n) => (isUgcMenuGroup(n) ? n.items : [n]))

const ENTRY_BY_KEY = new Map<string, UgcMenuEntry>(UGC_MENU_ENTRIES.map((e) => [e.key, e]))

/** その機能がいまメニューにあれば定義を返す（準備中なら undefined） */
export const findUgcMenuEntry = (key: UgcFeatureKey): UgcMenuEntry | undefined => ENTRY_BY_KEY.get(key)
